/** @format */

import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  PlusIcon,
  ArrowLeftIcon,
  AcademicCapIcon,
  ChartBarIcon,
  CurrencyDollarIcon,
  UserGroupIcon,
  StarIcon,
  ClockIcon,
  PlayIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  UsersIcon,
} from "@heroicons/react/24/outline";
import { useFirestore } from "../hooks/useFirestore";
import useCourseAuthors from "../hooks/useCourseAuthors";
import { getCourseCategoryDisplayName } from "../utils/categoryUtils";
import { useAuth } from "../contexts/AuthContext";
import toast from "react-hot-toast";
import CourseTable from "../components/CourseTable";
import CourseModal from "../components/CourseModal";
import AssignmentModal from "../components/AssignmentModal";
import EnrollmentModal from "../components/EnrollmentModal";
import ConfirmDialog from "../components/ConfirmDialog";
import CourseClassroomIndicator from "../components/CourseClassroomIndicator";

// Using shared CourseClassroomIndicator component with caching

const CourseCollectionPage = () => {
  const navigate = useNavigate();
  const { currentUser, isAdmin, userRole } = useAuth();
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [modalMode, setModalMode] = useState("add");
  const [deleteId, setDeleteId] = useState(null);
  const [assignmentModal, setAssignmentModal] = useState({
    open: false,
    course: null,
    mode: null,
  });
  const [enrollmentModal, setEnrollmentModal] = useState({
    open: false,
    course: null,
  });
  const [featuredRemovalDialog, setFeaturedRemovalDialog] = useState({
    open: false,
    course: null,
  });
  const [deleteLoading, setDeleteLoading] = useState(false);

  const {
    documents: courses,
    loading,
    addDocument,
    updateDocument,
    deleteDocument,
  } = useFirestore("courses");


  // Helper function to check if current user owns a course
  const isCourseOwner = useMemo(() => {
    return (course) => {
      if (!currentUser) return false;

      // More flexible ownership check
      const isOwner =
        // Check by Firebase UID
        course.userId === currentUser.uid ||
        course.author === currentUser.uid ||
        course.author_id === currentUser.uid ||
        // Check by email
        course.author === currentUser.email ||
        // Check by display name
        course.author === currentUser.displayName ||
        // Check if author field contains current user info (case-insensitive)
        (typeof course.author === "string" &&
          course.author
            .toLowerCase()
            .includes(currentUser.email?.toLowerCase())) ||
        (typeof course.author === "string" &&
          course.author
            .toLowerCase()
            .includes(currentUser.displayName?.toLowerCase()));

      return isOwner;
    };
  }, [currentUser, userRole]);

  // Filter documents based on user role
  const filteredDocuments = useMemo(() => {
    let filtered = [];

    if (isAdmin) {
      filtered = courses; // Admins can see all courses
    } else if (userRole === "author" && currentUser) {
      filtered = courses.filter((doc) => isCourseOwner(doc));
    } else if (userRole === "student" && currentUser) {
      // Students can see courses they're enrolled in
      filtered = courses.filter((doc) => {
        const isEnrolled =
          doc.enrolled_students?.includes(currentUser.uid) ||
          doc.students?.includes(currentUser.uid);
        return isEnrolled;
      });
    }

    return filtered;
  }, [
    courses,
    isAdmin,
    userRole,
    currentUser,
    isCourseOwner,
  ]);

  // Fetch course authors for statistics
  const { getAuthorId } = useCourseAuthors(filteredDocuments);

  // Calculate course statistics
  const courseStats = useMemo(() => {
    if (!filteredDocuments.length)
      return {
        total: 0,
        live: 0,
        draft: 0,
        archived: 0,
        totalStudents: 0,
        totalRevenue: 0,
        averageRating: 0,
        freeCount: 0,
        paidCount: 0,
        totalLessons: 0,
        averagePrice: 0,
        featuredCount: 0,
      };

    const stats = filteredDocuments.reduce(
      (acc, course) => {
        acc.total++;
        acc[course.status] = (acc[course.status] || 0) + 1;

        // Count featured courses
        if (course.featured) {
          acc.featuredCount++;
        }

        // Count students from course doc fields
        const studentCount = Array.isArray(course.enrolled_students)
          ? course.enrolled_students.length
          : typeof course.students === "number"
          ? course.students
          : Array.isArray(course.students)
          ? course.students.length
          : 0;
        acc.totalStudents += studentCount;
        acc.totalLessons += course.lessons_count || 0;

        // Track unique authors
        const authorId = getAuthorId(course);
        if (authorId) {
          acc.authorIds.add(authorId);
        }

        if (course.price_status === "free") {
          acc.freeCount++;
        } else {
          acc.paidCount++;
          const price = (() => {
            const basePrice = parseFloat(course.pricee) || 0;
            const booksTotal = Array.isArray(course.books)
              ? course.books.reduce(
                  (total, book) => total + (parseFloat(book?.amount) || 0),
                  0
                )
              : 0;
            return basePrice + booksTotal;
          })();
          acc.totalRevenue += price * studentCount;
          acc.priceSum += price;
        }

        if (course.rating > 0) {
          acc.ratingSum += parseFloat(course.rating);
          acc.ratedCount++;
        }

        return acc;
      },
      {
        total: 0,
        live: 0,
        draft: 0,
        archived: 0,
        totalStudents: 0,
        totalRevenue: 0,
        freeCount: 0,
        paidCount: 0,
        totalLessons: 0,
        averagePrice: 0,
        featuredCount: 0,
        authorIds: new Set(),
        priceSum: 0,
        ratingSum: 0,
        ratedCount: 0,
      }
    );

    // Calculate averages
    stats.averagePrice =
      stats.paidCount > 0 ? stats.priceSum / stats.paidCount : 0;
    stats.averageRating =
      stats.ratedCount > 0 ? stats.ratingSum / stats.ratedCount : 0;

    return stats;
  }, [filteredDocuments, getAuthorId]);

  const handleAdd = () => {
    setSelectedCourse(null);
    setModalMode("add");
    setModalOpen(true);
  };

  const handleEdit = (course) => {
    // Check if user can edit this course
    if (!isAdmin && userRole === "author" && currentUser) {
      const isOwner = isCourseOwner(course);

      if (!isOwner) {
        toast.error("You can only edit courses you have created.");
        return;
      }
    }

    setSelectedCourse(course);
    setModalMode("edit");
    setModalOpen(true);
  };

  const handleView = (course) => {
    setSelectedCourse(course);
    setModalMode("view");
    setModalOpen(true);
  };

  const handleAssignAuthor = (course) => {
    // Only admins can assign authors
    if (!isAdmin) {
      toast.error("Only administrators can assign authors to courses.");
      return;
    }
    setAssignmentModal({ open: true, course, mode: "author" });
  };

  const handleAssignStudents = (course) => {
    // Check if user can manage this course
    if (!isAdmin && userRole === "author" && currentUser) {
      const isOwner = isCourseOwner(course);

      if (!isOwner) {
        toast.error(
          "You can only manage students for courses you have created."
        );
        return;
      }
    }
    setEnrollmentModal({ open: true, course });
  };

  const handleAssignmentSave = async (updateData, courseId) => {
    try {
      await updateDocument(courseId, updateData);
      toast.success(`Assignment updated successfully!`);
      setAssignmentModal({ open: false, course: null, mode: null });
    } catch (error) {
      console.error("Error updating assignment:", error);
      toast.error("Failed to update assignment");
    }
  };

  const handleEnrollmentSuccess = (message) => {
    toast.success(message);
  };

  const handleDelete = (id) => {
    // Check if user can delete this course
    const course = filteredDocuments.find((c) => c.id === id);
    if (!course) return;

    if (!isAdmin && userRole === "author" && currentUser) {
      const isOwner = isCourseOwner(course);

      if (!isOwner) {
        toast.error("You can only delete courses you have created.");
        return;
      }
    }

    setDeleteId(id);
    setConfirmDialogOpen(true);
  };

  const handleToggleFeatured = async (courseId, featured) => {
    // Check if user can toggle featured status for this course
    const course = filteredDocuments.find((c) => c.id === courseId);
    if (!course) return;

    if (!isAdmin && userRole === "author" && currentUser) {
      const isOwner = isCourseOwner(course);

      if (!isOwner) {
        toast.error(
          "You can only toggle featured status for courses you have created."
        );
        return;
      }
    }

    try {
      await updateDocument(courseId, { featured });
      toast.success(
        featured
          ? "Course marked as featured successfully!"
          : "Course removed from featured successfully!"
      );
    } catch (error) {
      console.error("Error toggling featured status:", error);
      toast.error("Failed to update featured status");
    }
  };

  const handleRemoveFromFeatured = (course) => {
    setFeaturedRemovalDialog({
      open: true,
      course: course,
    });
  };

  const confirmRemoveFromFeatured = async () => {
    try {
      const { course } = featuredRemovalDialog;
      if (!course) return;

      await handleToggleFeatured(course.id, false);
      setFeaturedRemovalDialog({ open: false, course: null });
    } catch (error) {
      console.error("Error removing from featured:", error);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteId) return;

    setDeleteLoading(true);
    try {
      await deleteDocument(deleteId);
      setConfirmDialogOpen(false);
      setDeleteId(null);
    } catch (error) {
      console.error("Error deleting course:", error);
      alert("Failed to delete course. Please try again.");
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleSave = async (courseData, courseId) => {
    try {
      if (courseId) {
        // Check if user can edit this course
        if (!isAdmin && userRole === "author" && currentUser) {
          const course = filteredDocuments.find((c) => c.id === courseId);
          if (course) {
            const isOwner = isCourseOwner(course);

            if (!isOwner) {
              throw new Error("You can only edit courses you have created.");
            }
          }
        }
        await updateDocument(courseId, courseData);
      } else {
        // For new courses, ensure both userId and author are set
        if (currentUser) {
          courseData.userId = currentUser.uid;
          courseData.author =
            currentUser.email || currentUser.displayName || currentUser.uid;
        }
        await addDocument(courseData);
      }
    } catch (error) {
      console.error("Error saving course:", error);
      throw error;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-GH", {
      style: "currency",
      currency: "GHS",
    }).format(amount);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate("/")}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <ArrowLeftIcon className="h-4 w-4 mr-2" />
                Back to Dashboard
              </button>
              <div className="flex items-center space-x-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-r from-blue-500 to-indigo-600">
                  <AcademicCapIcon className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-gray-900">
                    Course Management
                  </h1>
                  <p className="text-sm text-gray-500">
                    Manage your course catalog
                  </p>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => navigate('/enrolled-students')}
                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <UsersIcon className="h-4 w-4 mr-2" />
                Enrolled Students
              </button>
              {(userRole === "author" || isAdmin) && (
                <button
                  onClick={handleAdd}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  Create Course
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Course Summary */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="bg-white shadow-lg rounded-xl p-6 mb-7">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                {userRole === "author"
                  ? "My Course Dashboard"
                  : isAdmin
                  ? "Course Management Dashboard"
                  : "Course Management Dashboard"}
              </h2>
              {userRole === "author" && currentUser && (
                <div className="space-y-2">
                  <p className="text-gray-600">
                    Welcome back,{" "}
                    <span className="font-medium text-blue-600">
                      {currentUser.email || currentUser.displayName}
                    </span>
                    ! Here are the courses you've created and manage.
                  </p>
                  <p className="text-sm text-gray-500">
                    You can create new courses, edit existing ones, and manage
                    student enrollments.
                  </p>
                </div>
              )}
              {isAdmin && (
                <div className="space-y-2">
                  <p className="text-gray-600">
                    You have full administrative access to all courses in the
                    system.
                  </p>
                  <p className="text-sm text-gray-500">
                    You can create, edit, delete, and manage all courses and
                    their authors. Use the "Create Course" button to add new courses.
                  </p>
                </div>
              )}
              {userRole === "student" && currentUser && (
                <div className="space-y-2">
                  <p className="text-gray-600">
                    Welcome,{" "}
                    <span className="font-medium text-green-600">
                      {currentUser.email || currentUser.displayName}
                    </span>
                    ! Here are the courses you're enrolled in.
                  </p>
                  <p className="text-sm text-gray-500">
                    You can view course content, participate in discussions, and
                    track your progress.
                  </p>
                </div>
              )}
            </div>
            {userRole === "author" && (
              <div className="text-right">
                <div className="text-2xl font-bold text-blue-600">
                  {courseStats.total}
                </div>
                <div className="text-sm text-gray-500">Total Courses</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Total Courses */}
          <div className="bg-white overflow-hidden shadow-lg rounded-xl">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <AcademicCapIcon className="h-8 w-8 text-blue-600" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      {userRole === "author" ? "My Courses" : "Total Courses"}
                    </dt>
                    <dd className="text-2xl font-bold text-gray-900">
                      {courseStats.total}
                    </dd>
                    {userRole === "author" && currentUser && (
                      <dd className="text-xs text-gray-500 mt-1">
                        Author: {currentUser.email || currentUser.displayName}
                      </dd>
                    )}
                  </dl>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 px-5 py-3">
              <div className="text-sm">
                <div className="flex justify-between items-center">
                  <span className="text-green-600 font-medium flex items-center">
                    <CheckCircleIcon className="h-4 w-4 mr-1" />
                    {courseStats.live} Live
                  </span>
                  <span className="text-yellow-600 font-medium flex items-center">
                    <ClockIcon className="h-4 w-4 mr-1" />
                    {courseStats.draft} Draft
                  </span>
                  <span className="text-orange-600 font-medium flex items-center">
                    <svg
                      className="h-4 w-4 mr-1"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                    {courseStats.featuredCount} Featured
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Total Students */}
          <div className="bg-white overflow-hidden shadow-lg rounded-xl">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <UserGroupIcon className="h-8 w-8 text-green-600" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Total Students
                    </dt>
                    <dd className="text-2xl font-bold text-gray-900">
                      {courseStats.totalStudents.toLocaleString()}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 px-5 py-3">
              <div className="text-sm">
                <span className="text-gray-600">
                  Avg:{" "}
                  {courseStats.total > 0
                    ? Math.round(courseStats.totalStudents / courseStats.total)
                    : 0}{" "}
                  per course
                </span>
              </div>
            </div>
          </div>

          {/* Revenue */}
          <div className="bg-white overflow-hidden shadow-lg rounded-xl">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <CurrencyDollarIcon className="h-8 w-8 text-purple-600" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Total Revenue
                    </dt>
                    <dd className="text-2xl font-bold text-gray-900">
                      {formatCurrency(courseStats.totalRevenue)}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 px-5 py-3">
              <div className="text-sm">
                <div className="flex justify-between items-center">
                  <span className="text-green-600 font-medium">
                    {courseStats.freeCount} Free
                  </span>
                  <span className="text-blue-600 font-medium">
                    {courseStats.paidCount} Paid
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Average Rating */}
          <div className="bg-white overflow-hidden shadow-lg rounded-xl">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <StarIcon className="h-8 w-8 text-yellow-500" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Average Rating
                    </dt>
                    <dd className="text-2xl font-bold text-gray-900">
                      {courseStats.averageRating.toFixed(1)}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 px-5 py-3">
              <div className="text-sm">
                <span className="text-gray-600">
                  {courseStats.totalLessons} total lessons
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Content Insights */}
        <div className="bg-white shadow-lg rounded-xl p-6 mb-8">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <ChartBarIcon className="h-5 w-5 mr-2" />
            Course Insights
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">
                {formatCurrency(courseStats.averagePrice)}
              </div>
              <div className="text-sm text-gray-500">Average Course Price</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {courseStats.total > 0
                  ? Math.round(courseStats.totalLessons / courseStats.total)
                  : 0}
              </div>
              <div className="text-sm text-gray-500">
                Average Lessons per Course
              </div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">
                {courseStats.total > 0
                  ? Math.round(
                      (courseStats.freeCount / courseStats.total) * 100
                    )
                  : 0}
                %
              </div>
              <div className="text-sm text-gray-500">Free Courses</div>
            </div>
          </div>
        </div>

        {/* Featured Courses Section */}
        {courseStats.featuredCount > 0 && (
          <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 shadow-lg rounded-xl p-6 mb-8">
            <h3 className="text-lg font-medium text-yellow-900 mb-4 flex items-center">
              <svg
                className="h-5 w-5 mr-2"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
              Featured Courses ({courseStats.featuredCount})
              <div className="ml-2 relative group">
                <svg
                  className="h-4 w-4 text-yellow-600 cursor-help"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                  Click X button to remove from featured
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                </div>
              </div>
            </h3>
            <p className="text-yellow-800 text-sm mb-4">
              These courses are highlighted and promoted to users. Featured
              courses typically have high quality content, good ratings, or
              special significance. Click the X button on any course to remove
              it from featured status.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredDocuments
                .filter((course) => course.featured)
                .slice(0, 6)
                .map((course) => (
                  <div
                    key={course.id}
                    className="group bg-white rounded-lg border border-yellow-200 shadow-sm hover:shadow-md transition-shadow duration-200 overflow-hidden cursor-pointer hover:border-yellow-300 transition-all duration-200"
                    onClick={() => handleView(course)}
                  >
                    {/* Course Image */}
                    <div className="relative h-32 bg-gradient-to-br from-blue-100 to-purple-100 group-hover:scale-105 transition-transform duration-200">
                      {course.image_url ? (
                        <img
                          src={course.image_url}
                          alt={course.name || "Course"}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = "none";
                            e.target.nextSibling.style.display = "flex";
                          }}
                        />
                      ) : null}
                      <div
                        className={`w-full h-full flex items-center justify-center ${
                          course.image_url ? "hidden" : "flex"
                        }`}
                      >
                        <svg
                          className="h-12 w-12 text-blue-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                          />
                        </svg>
                      </div>

                      {/* Featured Badge Overlay */}
                      <div className="absolute top-2 right-2">
                        <span className="inline-flex items-center px-2 py-1 text-xs font-semibold bg-gradient-to-r from-yellow-400 to-orange-500 text-white rounded-full shadow-sm">
                          <svg
                            className="h-3 w-3 mr-1"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                          Featured
                        </span>
                      </div>

                      {/* Featured Toggle Button */}
                      <div className="absolute top-2 left-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveFromFeatured(course);
                          }}
                          className="p-1.5 bg-white/90 hover:bg-white text-yellow-600 hover:text-red-600 rounded-full shadow-sm transition-all duration-200 hover:scale-110 hover:shadow-md"
                          title="Remove from featured"
                        >
                          <svg
                            className="h-4 w-4"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    {/* Course Content */}
                    <div className="p-4">
                      <h4 className="font-medium text-gray-900 text-sm mb-2 line-clamp-2 leading-tight group-hover:text-blue-600 transition-colors duration-200">
                        {course.name || "Untitled Course"}
                      </h4>
                      <p className="text-gray-600 text-xs mb-2 line-clamp-2">
                        {course.meta?.summary ||
                          course.meta?.description?.replace(/<[^>]*>/g, "") ||
                          "No description available"}
                      </p>

                      {/* Category */}
                      {course.cat_id && (
                        <div className="mb-2">
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {getCourseCategoryDisplayName(course.cat_id)}
                          </span>
                        </div>
                      )}

                      {/* Classroom Indicator */}
                      <CourseClassroomIndicator courseId={course.id} />

                      {/* Course Meta */}
                      <div className="flex items-center justify-between text-xs text-gray-500 mb-2">
                        <span
                          className={`px-2 py-1 rounded-full ${
                            course.status === "live"
                              ? "bg-green-100 text-green-700"
                              : course.status === "draft"
                              ? "bg-yellow-100 text-yellow-700"
                              : "bg-gray-100 text-gray-700"
                          }`}
                        >
                          {course.status}
                        </span>
                        <span className="font-medium">
                          {formatCurrency(
                            (parseFloat(course.pricee) || 0) +
                              (Array.isArray(course.books)
                                ? course.books.reduce(
                                    (total, book) =>
                                      total + (parseFloat(book?.amount) || 0),
                                    0
                                  )
                                : 0)
                          )}
                        </span>
                      </div>

                      {/* Course Stats */}
                      <div className="flex items-center justify-between text-xs text-gray-400">
                        <span className="flex items-center">
                          <svg
                            className="h-3 w-3 mr-1"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"
                            />
                          </svg>
                          {Array.isArray(course.enrolled_students)
                            ? course.enrolled_students.length
                            : typeof course.students === "number"
                            ? course.students
                            : Array.isArray(course.students)
                            ? course.students.length
                            : 0} students
                        </span>
                        <span className="flex items-center">
                          <svg
                            className="h-3 w-3 mr-1"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                            />
                          </svg>
                          {course.lessons_count || 0} lessons
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* Course Table Header */}
        <div className="bg-white shadow-lg rounded-xl p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {userRole === "author" ? "My Courses" : "All Courses"}
              </h3>
              {userRole === "author" && currentUser && (
                <p className="text-sm text-gray-600">
                  Showing courses where you are the author. You can create,
                  edit, and manage these courses.
                </p>
              )}
              {isAdmin && (
                <p className="text-sm text-gray-600">
                  Showing all courses in the system. You have full access to
                  manage all courses.
                </p>
              )}
              {userRole === "student" && currentUser && (
                <p className="text-sm text-gray-600">
                  Showing courses you are enrolled in. You can view and
                  participate in these courses.
                </p>
              )}
            </div>
            {(userRole === "author" || isAdmin) && (
              <button
                onClick={() => {
                  setModalMode("add");
                  setSelectedCourse(null);
                  setModalOpen(true);
                }}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                Create New Course
              </button>
            )}
          </div>
        </div>

        {/* Course Table */}
        <CourseTable
          courses={filteredDocuments}
          onView={handleView}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onAssignAuthor={handleAssignAuthor}
          onAssignStudents={handleAssignStudents}
          onToggleFeatured={handleToggleFeatured}
          loading={loading}
        />

        {/* Course Modal */}
        <CourseModal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          onSave={handleSave}
          course={selectedCourse}
          mode={modalMode}
        />

        {/* Assignment Modal (Author only) */}
        <AssignmentModal
          isOpen={assignmentModal.open}
          onClose={() =>
            setAssignmentModal({ open: false, course: null, mode: null })
          }
          onSave={handleAssignmentSave}
          course={assignmentModal.course}
          mode={assignmentModal.mode}
        />

        {/* Enrollment Modal (Students) */}
        <EnrollmentModal
          isOpen={enrollmentModal.open}
          onClose={() => setEnrollmentModal({ open: false, course: null })}
          course={enrollmentModal.course}
          onSuccess={handleEnrollmentSuccess}
        />

        {/* Confirm Delete Dialog */}
        <ConfirmDialog
          isOpen={confirmDialogOpen}
          onClose={() => setConfirmDialogOpen(false)}
          onConfirm={handleConfirmDelete}
          title="Delete Course"
          message="Are you sure you want to delete this course? This action cannot be undone."
          loading={deleteLoading}
        />

        {/* Confirm Remove from Featured Dialog */}
        <ConfirmDialog
          isOpen={featuredRemovalDialog.open}
          onClose={() =>
            setFeaturedRemovalDialog({ open: false, course: null })
          }
          onConfirm={confirmRemoveFromFeatured}
          title="Remove from Featured"
          message={`Are you sure you want to remove "${
            featuredRemovalDialog.course?.name || "this course"
          }" from featured status? This will no longer highlight it to users.`}
          confirmText="Remove from Featured"
          cancelText="Keep Featured"
          confirmButtonClass="bg-yellow-600 hover:bg-yellow-700"
        />
      </div>
    </div>
  );
};

export default CourseCollectionPage;
